import { Box, Modal } from "@mui/material";
import { type FC, useEffect, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import CommunicationThread from "../CommunicationThread";
import { usePostStaffMessage, useThreadMessagesQuery, useUpdateThreadReadStatus } from "../hooks";
import { combineStaffCommunicationHistory } from "../utils";

type Props = {
	open: boolean;
	nurseId: number;
	onClose: () => void;
};

export const NurseChatDialog: FC<Props> = ({ open, nurseId, onClose }) => {
	const { getNurseById, chosenDepartment } = useHospitalData();
	const nurse = getNurseById(nurseId);
	const departmentId = chosenDepartment.department_id;

	const threadHistoryQuery = useThreadMessagesQuery(departmentId, open ? nurse?.userId : undefined);
	const { mutate: updateThreadReadStatus } = useUpdateThreadReadStatus(departmentId);
	const { mutate: postStaffMessage } = usePostStaffMessage();

	// Mark this nurse's thread as read whenever the dialog opens.
	useEffect(() => {
		if (open && nurse?.userId) {
			updateThreadReadStatus(nurse.userId);
		}
	}, [open, nurse?.userId, updateThreadReadStatus]);

	const thread = useMemo(() => {
		if (!nurse?.userId || !threadHistoryQuery.data) return [];
		return combineStaffCommunicationHistory(threadHistoryQuery.data.pages, departmentId);
	}, [departmentId, nurse?.userId, threadHistoryQuery.data]);

	const handlePostNewMessage = (message: string) => {
		if (!nurse?.userId || !message.trim()) return;
		postStaffMessage({ message, userIds: [nurse.userId], tempId: uuidv4() });
	};

	if (!nurse) return null;

	return (
		<Modal open={open} onClose={onClose}>
			<Box
				sx={{
					display: "flex",
					height: "100vh",
					width: "100vw",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						bgcolor: "#fff",
						borderRadius: 2,
						padding: 2,
						width: { xs: "92vw", md: 640 },
						height: { xs: "85vh", md: 700 },
						maxHeight: "90vh",
					}}
				>
					<CommunicationThread
						threads={thread}
						nurse={nurse}
						clearUserSelection={onClose}
						postNewMessage={handlePostNewMessage}
						hasOlderMessages={Boolean(threadHistoryQuery.hasNextPage)}
						loadOlderMessages={threadHistoryQuery.fetchNextPage}
						isLoadingOlderMessages={threadHistoryQuery.isFetchingNextPage}
						isThreadLoading={threadHistoryQuery.isLoading}
					/>
				</Box>
			</Box>
		</Modal>
	);
};
